import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { DollarSign, TrendingUp, MousePointer, ShoppingCart, ArrowUpRight } from 'lucide-react';
import { commissionTiers } from '../mock';
import { authAPI, userAPI } from '../services/apiService';
import { toast } from 'sonner';

// Import new dashboard components
import EarningsChart from '../components/dashboard/EarningsChart';
import ActivityTimeline from '../components/dashboard/ActivityTimeline';
import SocialShareButtons from '../components/dashboard/SocialShareButtons';
import NotificationCenter from '../components/dashboard/NotificationCenter';
import PerformanceComparison from '../components/dashboard/PerformanceComparison';

const DashboardEnhanced = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    loadUserData();
  }, [navigate]);

  const loadUserData = async () => {
    try {
      const [userData, userStats, referral] = await Promise.all([
        authAPI.getCurrentUser(),
        userAPI.getStats(),
        userAPI.getReferralLink()
      ]);
      
      setUser(userData);
      setStats(userStats);
      setReferralData(referral);
    } catch (error) {
      toast.error('Failed to load user data');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading || !user || !stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const currentTier = commissionTiers.find(t => t.tier === stats.currentTier);

  return (
    <div className="min-h-screen py-12 bg-gradient-to-br from-red-50 via-orange-50 to-pink-50">
      <div className="max-w-7xl mx-auto px-6">
        {/* Welcome Header */}
        <div className="mb-8 bg-white/80 backdrop-blur-sm p-6 rounded-2xl border-4 border-red-300 shadow-xl">
          <h1 className="text-4xl font-bold mb-2" style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            Welcome back, {user.firstName}! 🎉
          </h1>
          <p className="text-gray-700 text-lg font-medium">Here's your enhanced referral performance dashboard</p>
          {referralData && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
              <p className="text-sm font-semibold text-blue-900">Your Referral Code:</p>
              <p className="text-2xl font-bold text-blue-600">{referralData.referralCode}</p>
            </div>
          )}
        </div>

        {/* Stats Cards Row */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-3 border-red-300 hover:border-red-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm hover:scale-105">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <DollarSign className="h-6 w-6 text-teal-600" />
                </div>
                <ArrowUpRight className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-sm text-gray-600 mb-1">Available Balance</p>
              <p className="text-3xl font-bold text-gray-900">₹{stats.balance.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-yellow-300 hover:border-yellow-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm hover:scale-105">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
                <ArrowUpRight className="h-5 w-5 text-orange-500" />
              </div>
              <p className="text-sm text-gray-600 mb-1">Pending</p>
              <p className="text-3xl font-bold text-gray-900">₹{stats.pendingBalance.toLocaleString()}</p>
              <p className="text-xs text-gray-500 mt-1">Awaiting confirmation</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-pink-300 hover:border-pink-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm hover:scale-105">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <MousePointer className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">Total Clicks</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalClicks.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="border-3 border-red-300 hover:border-red-500 transition-all duration-300 shadow-lg bg-white/90 backdrop-blur-sm hover:scale-105">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ShoppingCart className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-1">Conversions</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalConversions}</p>
            </CardContent>
          </Card>
        </div>

        {/* Row 1: Performance Comparison & Tier */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="md:col-span-2">
            <PerformanceComparison />
          </div>
          
          <Card className="border-4 shadow-lg bg-white/90 backdrop-blur-sm" style={{ borderColor: currentTier?.color }}>
            <CardContent className="p-6">
              <p className="text-sm text-gray-600 mb-2">Current Tier</p>
              <p className="text-3xl font-bold mb-2" style={{ color: currentTier?.color }}>{currentTier?.name}</p>
              <p className="text-gray-600 mb-3">{currentTier?.salesRange}</p>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Commission Rate</p>
                  <p className="text-2xl font-bold" style={{ color: currentTier?.color }}>{currentTier?.rate}%</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">This Month</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.salesThisMonth}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">Total Earned: ₹{stats.lifetimeEarnings.toLocaleString()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Row 2: Earnings Chart (Full Width) */}
        <div className="mb-8">
          <EarningsChart />
        </div>

        {/* Row 3: Social Share & Notifications */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {referralData && (
            <SocialShareButtons 
              referralLink={referralData.referralLink}
              referralCode={referralData.referralCode}
            />
          )}
          <NotificationCenter />
        </div>

        {/* Row 4: Activity Timeline (Full Width) */}
        <div className="mb-8">
          <ActivityTimeline />
        </div>
      </div>
    </div>
  );
};

export default DashboardEnhanced;
